import React from 'react'
import i18n from 'i18next'
import { initReactI18next, withSSR } from 'react-i18next'
import { I18nInternalContext } from '../../internal/I18nInternalContext'
import {
  EXTERNAL_REGISTER_RESOURCE_MAP,
  I18N_LOADING_COUNT,
} from '../../internal/I18nConst'
import { createExtraInstanceHelper } from './util'

const EXTRA_I18N_CONF_STORE_KEY = Symbol('wrapExApp/redux/i18n_conf_store')

export default function withI18n(App) {
  const i18nInstanceHelper = createExtraInstanceHelper(withI18n, appendCtx)

  // i18n ssr 需要额外包装一层
  const ExtendedApp = withSSR()(App)

  return class extends React.PureComponent {
    static displayName = `fwAppWithI18n(${
      ExtendedApp.displayName || ExtendedApp.name || 'App'
    })`

    static async getInitialProps(appCtx) {
      const injectedInitialProps = await i18nInstanceHelper.handleOnGetInitialProps(
        appCtx,
        async () => {
          const result = initI18nInstance(appCtx.ctx, undefined)
          await preloadI18nResource(result.i18nInstance, 'global')
          return result
        }
      )

      // 完成 next 的 getInitialProps 生命周期
      let initialProps = {}
      // HACK 直接使用 ExtendedApp 执行会有报错，不确定原因，直接使用 App 绕过
      if ('getInitialProps' in App) {
        initialProps = await App.getInitialProps(appCtx)
      }

      return {
        ...initialProps,
        ...injectedInitialProps,
        // i18n 需要字段，同时用于同步 server lang 至 browser
        initialI18nConf: appCtx.ctx.i18n[EXTRA_I18N_CONF_STORE_KEY],
        // i18n 需要字段，同时用于同步 server 已加载语言包
        initialI18nStore: appCtx.ctx.i18n.store.data,
      }
    }

    // 无论 server、browser 均执行
    // getInitialProps 结果将作为 props 传入
    // 但 browser 初始化 ssr 渲染页面时，仅包含可序列化状态，缺少各复杂对象，需手动重建
    constructor(props, ...rest) {
      super(props, ...rest)

      i18nInstanceHelper.handleOnConstructor(props, () => {
        const result = initI18nInstance(props.ctx, props.initialI18nConf)
        // 预加载但不阻塞
        preloadI18nResource(result.i18nInstance, 'global')
        return result
      })
    }

    render() {
      i18nInstanceHelper.handleAppendOnRender(this.props)
      // initialI18nConf 已在 getInitialProps 返回
      return (
        <I18nInternalContext.Provider
          value={{
            namespace: 'global',
            isI18nResourceLoading: this.props.ctx.i18n[I18N_LOADING_COUNT],
          }}
        >
          <ExtendedApp
            {...this.props}
            i18n={this.props.ctx.i18n}
            i18nLocale={this.props.ctx.locale}
          />
        </I18nInternalContext.Provider>
      )
    }
  }

  // 初始化全局 i18n 实例
  function initI18nInstance(ctx, initialI18nConf) {
    const i18nConf = ctx.__AL_EX_internalScope.globalConfig.i18n.resolve(
      ctx,
      ctx.lifecycleStatus,
      initialI18nConf
    )

    const i18nInstance = makeI18n(i18nConf, ctx)
    i18nInstance.changeLanguage(i18nConf.lang) // 未知原因，需要手动设置一次，否则初始状态无语言

    return { i18nInstance, i18nConf }
  }

  // 创建 i18n 实例
  function makeI18n(i18nConf, ctx) {
    const i18nInstance = i18n.createInstance()
    const i18nResource = {
      global: ctx.__AL_EX_internalScope.globalConfig.i18n.resource,
    }

    i18nInstance[I18N_LOADING_COUNT] = 0

    class I18nLoader {
      static type = 'backend'

      constructor(services, options = {}) {
        this.init(services, options)
        this.type = 'backend'
      }

      init(services, options = {}) {}

      read(language, namespace, callback) {
        // 先检查是否已存在于 store，跳过加载
        // withSSR 在 render 才工作，时机太晚，直接检查全局对象
        if (process.browser) {
          const store = window.__NEXT_DATA__.props.initialI18nStore
          const storeData = store[language] && store[language][namespace]
          if (storeData) {
            return storeData
          }
        }

        // 不存在时从 () => import('/xx.json') 加载
        // 留意，涉及异步时序，可能并行加载，故用 count 计数
        const load =
          i18nResource[namespace] && i18nResource[namespace][language]

        if (load) {
          if (typeof load.then === 'function') {
            // 异步
            i18nInstance[I18N_LOADING_COUNT]++
            Promise.resolve(load)
              .then((data = {}) => {
                i18nInstance[I18N_LOADING_COUNT]--
                callback(null, data)
              })
              .catch((err) => {
                i18nInstance[I18N_LOADING_COUNT]--
                console.error(err)
                callback(null, {}) // 失败时仍回调，业务继续加载
              })
          } else {
            // 同步，不需要在额外异步。实际测试结果看，SSR时 Component 能够正确渲染
            callback(null, load)
          }
        } else {
          console.error(
            `语言包加载失败，当前语言为${language}，namespace为${namespace}`
          )
          callback(null, {}) // 失败时仍回调，业务继续加载
        }
      }

      save(language, namespace, data) {}
    }

    i18nInstance
      .use(initReactI18next) // passes i18n down to react-i18next
      .use(I18nLoader)
      .init({
        debug: false,
        ns: ['global'],
        defaultNS: 'global',
        lng: i18nConf.lang,
        fallbackLng: [], // 不理解行为。目前未等待语言包加载完成，而是立即加载 fallback 语言，先设置为 [] 禁用 fallback
        interpolation: {
          escapeValue: false,
          format: i18nConf.format,
        },
        react: {
          // server 不等待，否则无法正确渲染。需提前手动加载完成
          // browser 不等待，保持与 server 相同逻辑，并尽快展示内容
          wait: false,
          // 禁用 suspense，即使语言包加载中也先渲染节点
          useSuspense: false,
        },
        parseMissingKeyHandler: function (key) {
          // withSSR 无法保证 render 前语言包已就绪
          // 使得，页面存在 tReady false => true 的中间态
          // 中间态将渲染为 missingKey
          // 直接渲染为 <span>{''}</span> 将导致页面抖动（''无高度），必须使用 FormattedMessage 渲染
          return i18nInstance[I18N_LOADING_COUNT] > 0 ? '' : key
        },
      })
    // 额外注入方法，由于支持 next/al-ex withNamespace 注入 import() 语言包
    i18nInstance[EXTERNAL_REGISTER_RESOURCE_MAP] = function (
      namespace,
      resourceMap
    ) {
      // TODO 要优化
      // if (!(process.env.BUILD_MODE === 'export' && process.server)) {
      //   if (ctx.i18n.store.hasResourceBundle(ctx.i18n.language, namespace)) {
      //     ctx.i18n.store.removeResourceBundle(ctx.i18n.language, namespace)
      //   }
      // }
      i18nResource[namespace] = resourceMap
    }
    return i18nInstance
  }

  // 预加载指定语言包
  async function preloadI18nResource(i18nInstance, namespace) {
    // 立即加载 global
    if (!process.browser) {
      // server 时等待加载完成
      await new Promise((resolve) => {
        // i18n 内部已容错，加载不存在的 namespace 不会报错
        i18nInstance.loadNamespaces(namespace, resolve)
      })
    } else {
      // browser 预加载，但不阻塞完成
      i18nInstance.loadNamespaces(namespace)
    }
  }

  function appendCtx(ctx, { i18nInstance, i18nConf }) {
    ctx.i18n = i18nInstance
    ctx.i18n[EXTRA_I18N_CONF_STORE_KEY] = i18nConf
    ctx.locale = i18nConf.locale
  }
}
